import { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { useNavigate, useParams } from 'react-router-dom'
import '../../../assets/styles/custom-styles.css'
import useForm from '../../../hooks/useForm'
import { detailsOrderServiceGlobalFields } from '../../../constants/forms/orderServiceFields'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import DetailsSectionRenderer from '../../../components/DetailsSectionRenderer'
import {
  faBoxOpen,
  faCheckCircle,
  faEye,
  faMinus,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import PageHeader from '../../../components/PageHeader'
import { entities } from '../../../constants/entities'
import { formatDateToInput } from '../../../utils/formatDateToInput'
import ListHeader from '../../../components/ListHeader'
import { usePermissions } from '../../../hooks/usePermissions'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import useAction from '../../../hooks/useAction'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { genericStatusColorMap } from '../../../utils/statusColorMap'
import {
  GenericStatusEnum,
  GenericStatusEnumById,
} from '../../../enums/generic-status-enum'
import { Alert, Box, Typography } from '@mui/material'
import { useSectionFields } from '../../../hooks/useSectionFields'
import {
  getOrderSituationLabel,
  OrderSituationEnum,
} from '../../../enums/order-enum'

// Badge da coluna "Situação" (Sem estoque / Com estoque) dos itens da requisição
const renderStockSituation = (value) => (
  <span
    className={`badge ${value === 'Sem estoque' ? 'bg-danger' : 'bg-success'}`}>
    {value || '-'}
  </span>
)

const DetailsOrderServicesPage = () => {
  const { id: serviceOrderId } = useParams()
  const navigate = useNavigate()
  const { canAccess } = usePermissions()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()
  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    del: baseServiceDel,
    deleteWithParams: baseServiceDeleteWithParams,
    put: baseServicePut,
  } = useBaseService(navigate)

  const { formData, setFormData } = useForm(
    setDefaultFieldValues(detailsOrderServiceGlobalFields)
  )
  const [contractId, setContractId] = useState(null)

  const [equipColumns, setEquipColumns] = useState([])

  const {
    selectedItem,
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    action,
  } = useAction(navigate)

  const {
    filters: equipmentsFilters,
    setFilters: equipmentsApplyUrlFilters,
    pagination: equipmentsPagination,
    changePage: equipmentsChangePage,
    setPagination: equipmentsSetPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    `contractOsItems-${contractId}-${serviceOrderId}`
  )

  const {
    filters: productsFilters,
    setFilters: productsApplyUrlFilters,
    pagination: productsPagination,
    changePage: productsChangePage,
    setPagination: productsSetPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    `contractOsProducts-${contractId}-${serviceOrderId}`
  )

  const {
    handleFilterApply: handleEquipmentsFilterApply,
    handleFilterClear: handleEquipmentsFilterClear,
  } = useFilterHandlers(equipmentsApplyUrlFilters)

  const {
    handleFilterApply: handleProductsFilterApply,
    handleFilterClear: handleProductsFilterClear,
  } = useFilterHandlers(productsApplyUrlFilters)

  const { localFilterValues } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleEquipmentsFilterApply,
    handleEquipmentsFilterClear,
    handleProductsFilterApply,
    handleProductsFilterClear
  )

  // --- Query para buscar os detalhes da Ordem de Serviço (OS Principal) ---
  const {
    data: osDetails,
    isLoading: isLoadingOsDetails,
    isError: isErrorOsDetails,
    error: osDetailsError,
  } = useQueryWithLoader({
    queryKey: ['osDetails', serviceOrderId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.serviceOrders.getById(serviceOrderId)
      )
      if (!response.result) {
        throw new Error('Resposta inválida ao buscar detalhes da Requisição.')
      }
      return response.result
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!serviceOrderId,
  })

  const blockAction = useMemo(
    () => osDetails?.situation === OrderSituationEnum.CANCELLED,
    [osDetails]
  )

  useEffect(() => {
    if (osDetails) {
      setContractId(osDetails.contract_id)
      setFormData({
        id: osDetails.id,
        contract: osDetails.contract?.number || '-',
        recipient_customer:
          `${osDetails.recipient_customer?.id} - ${osDetails.recipient_customer?.alias}` ||
          '-',
        movement_type: osDetails.movement_type?.name || '-',
        status: osDetails.status?.name || '-',
        destination_order: osDetails.destination_address?.alias || '-',
        mode_transport: osDetails.mode_transport?.name || '-',
        deadline: osDetails.deadline
          ? formatDateToInput(osDetails.deadline.split(' ')[0])
          : '-',
        details: osDetails.details || '-',
        user: osDetails.user?.name || '-',
        changed_by: osDetails.changed_by?.name || '-',
        situation: getOrderSituationLabel(osDetails.situation) || '-',
        created_at: osDetails.created_at
          ? formatDateToInput(osDetails.created_at)
          : '-',
      })
    }

    if (isErrorOsDetails && osDetailsError) {
      showNotification(
        'error',
        `Erro ao carregar detalhes da OS: ${
          osDetailsError.response?.data?.message || osDetailsError.message
        }`
      )
      console.error('Erro ao carregar detalhes da OS:', osDetailsError)
    }
  }, [
    isErrorOsDetails,
    osDetails,
    osDetailsError,
    setFormData,
    showNotification,
  ])

  const {
    data: osItensEquipmentsResponse,
    isLoading: isLoadingOsItensEquipments,
    isFetching: isFetchingOsItensEquipments,
  } = useQueryWithLoader({
    queryKey: [
      'contractOsItemsEquipments',
      contractId,
      serviceOrderId,
      equipmentsFilters,
      equipmentsPagination,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        ...equipmentsFilters,
        type: 'equipment',
      })
      const params = {
        filters: filtersTransformed,
        page: equipmentsPagination.page,
        perPage: equipmentsPagination.perPage,
        sort: {
          column: 'id',
          order: 'asc',
        },
      }

      if (!contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.serviceOrders.items(serviceOrderId),
          params
        )
        return response.result.data
      } else if (contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.contracts.contractServiceOrderItems(
            contractId,
            serviceOrderId
          ),
          params
        )
        return response.result.data
      }
    },
    enabled: !!contractId || !!serviceOrderId,
    refreshOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    select: (data) => ({
      equipments: data.map((item) => {
        const atendedItem = item.quantity - item.fulfilled_quantity

        const extras_list = (item.extra_equipments || []).map((extra) => {
          const requested = extra.pivot?.quantity || 0
          const removed = extra.pivot?.removed_quantity || 0

          // Cálculo da quantidade atendida (mantendo sua lógica original)
          const atendedExtra = (item.movements || [])
            .filter((m) => !m.deleted_at)
            .reduce((acc, movement) => {
              const extrasInMovement = (movement.items || []).filter(
                (mItem) => {
                  return (
                    !mItem.deleted_at &&
                    mItem.extra_id === extra?.pivot?.id &&
                    mItem.service_order_item_id === item.id
                  )
                }
              ).length
              return acc + extrasInMovement
            }, 0)

          return {
            id: extra.pivot?.id,
            name: extra.name || '-',
            quantity: extra.pivot?.quantity || 0,
            progress_label: `${atendedExtra} / ${requested - removed}`,
            atended_quantity: atendedExtra,
            fulfilled_quantity: extra.pivot?.fulfilled_quantity || 0,
            removed_quantity: removed,
          }
        })

        const max_extra_atended =
          extras_list.length > 0
            ? Math.max(...extras_list.map((e) => e.atended_quantity))
            : 0

        return {
          id: item.id,
          movementsTypes: item.movement_type?.name || '-',
          department: item.department?.name || '-',
          statusOsItem: GenericStatusEnumById[item.status_id] || '-',
          statusOsItemId: item.status_id,
          contract_item: (() => {
            const alias = item.contract_item?.alias
            const description = item.contract_item?.description
            if (alias && description && alias !== description) {
              return `${alias} - ${description}`
            }
            if (alias || description) return alias || description
            if (item.product_id || item.product) return 'Insumos'
            if (
              item.type?.is_kit ||
              item.equipment?.type?.is_kit ||
              item.extra_equipments?.length
            ) {
              return 'Kits'
            }
            return '-'
          })(),
          address: item.address?.formatted_address || '-',
          equipment:
            `${item.equipment?.number} - ${item.equipment?.name}` || '-',
          quantity: item.quantity || 0,
          type: item.type?.name || '-',
          fulfilled_quantity: item.fulfilled_quantity || 0,
          //! quantidade atendida sera a quantidade de items de todos os movimentos que nao tem extra_id
          atended_quantity: atendedItem,
          progress_label: `${atendedItem} / ${item.quantity}`,
          isDeleted: !!item.deleted_at,
          stockStatus: item.stock_status || 'in_stock',
          stockSituation:
            (item.stock_status || 'in_stock') === 'awaiting_purchase'
              ? 'Sem estoque'
              : 'Com estoque',
          is_exchange: item.is_exchange,
          exchange_approval_status: item.exchange_approval_status,
          exchangeApprovalStatus: !item.is_exchange
            ? '-'
            : ({
                pending: 'Pendente',
                approved: 'Aprovado',
                rejected: 'Reprovado',
              }[(item.exchange_approval_status || '').toString().toLowerCase()] ||
              '-'),
          extras_list: extras_list,
          max_extra_atended: max_extra_atended,
        }
      }),
      totalPages: data.last_page,
    }),
  })

  const { equipments = [], totalPages: equipmentsTotalPages = 1 } =
    osItensEquipmentsResponse || {}

  const equipmentCollapseConfig = {
    titles: ['Kit de Equipamentos'],
    key: ['extras_list'],
    columns: [
      [
        { key: 'extras_list.name', label: 'Nome' },
        {
          key: 'extras_list.progress_label',
          label: 'Progresso (Movimentado)',
          numeric: true,
        },
      ],
    ],
    collapseActions: {
      onAdd: (item, parentRow) => {
        showConfirmation(
          item, // Passamos o limite para o modal
          `Informe quantos "${item.name}" deseja retornar:`,
          async (quantityValue) => {
            await restoreExtraMutation.mutateAsync({
              extra: item,
              parentRow,
              quantityToRestore: quantityValue,
            })
          },
          { modalAction: 'restoreExtra' }
        )
      },
      showOnAdd: (extra) => extra.removed_quantity > 0,
      addIcon: faPlus,
      onDelete: (item, parentRow) => {
        showConfirmation(
          item,
          `Informe quantos "${item.name}" deseja remover:`,
          async (quantityValue) => {
            try {
              if (!quantityValue) {
                showNotification('warning', 'Informe uma quantidade válida.')
                return
              }

              await deleteExtraMutation.mutateAsync({
                extra: item,
                parentRow,
                quantityToRemove: quantityValue,
              })
            } catch (error) {
              console.error('Exclusão cancelada ou falhou:', error)
            }
          },
          { modalAction: 'deleteExtra' }
        )
      },
      deleteIcon: faMinus,
      showOnDelete: (extra) => extra.fulfilled_quantity > 0,
    },
  }

  const {
    data: osItensProductsResponse,
    isLoading: isLoadingOsItensProducts,
    isFetching: isFetchingOsItensProducts,
  } = useQueryWithLoader({
    queryKey: [
      'contractOsItemsProducts',
      contractId,
      serviceOrderId,
      productsFilters,
      productsPagination,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        ...productsFilters,
        type: 'product',
      })
      const params = {
        filters: filtersTransformed,
        page: productsPagination.page,
        perPage: productsPagination.perPage,
      }

      if (!contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.serviceOrders.items(serviceOrderId),
          params
        )
        return response.result.data
      } else if (contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.contracts.contractServiceOrderItems(
            contractId,
            serviceOrderId
          ),
          params
        )
        return response.result.data
      }
    },
    enabled: !!contractId || !!serviceOrderId,
    refreshOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    select: (data) => ({
      products: data.map((item) => {
        const quantityAtended =
          item.quantity_product - item.fulfilled_quantity_product

        return {
          id: item.id,
          movementsTypes: item.movement_type?.name || '-',
          department: item.department?.name || '-',
          statusOsItem: GenericStatusEnumById[item.status_id] || '-',
          statusOsItemId: item.status_id,
          product: item.product?.name || '-',
          quantity_product: item.quantity_product ?? 0,
          quantity_remaining: item.quantity_product - quantityAtended,
          progress_label: `${item.quantity_product - item.fulfilled_quantity_product} / ${item.quantity_product}`,
          isDeleted: !!item.deleted_at,
          stockStatus: item.stock_status || 'in_stock',
          stockSituation:
            (item.stock_status || 'in_stock') === 'awaiting_purchase'
              ? 'Sem estoque'
              : 'Com estoque',
        }
      }),
      totalPages: data.last_page,
    }),
  })

  const productsColumns = [
    { key: 'movementsTypes', label: 'Tipo de Movimento' },
    { key: 'product', label: 'Insumo', width: '40%' },
    { key: 'progress_label', label: 'Progresso (Movimentado)' },
    { key: 'department', label: 'Departamento' },
    { key: 'statusOsItem', label: 'Status' },
    {
      key: 'stockSituation',
      label: 'Situação',
      render: renderStockSituation,
    },
  ]

  const { products = [], totalPages: productsTotalPages = 1 } =
    osItensProductsResponse || {}

  const isPageLoading =
    isLoadingOsDetails || isLoadingOsItensProducts || isLoadingOsItensEquipments

  // --- Efeito para controlar o carregamento da página ---
  useEffect(() => {
    if (isPageLoading) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isPageLoading, showLoader, hideLoader])

  // --- Efeito para configurar as colunas de acordo com o tipo de movimento ---
  useEffect(() => {
    if (!osDetails) return

    const type = osDetails.movement_type?.name // String vinda do Accessor Laravel

    // Configuração padrão de colunas comuns
    const commonEquip = [
      { key: 'department', label: 'Departamento' },
      { key: 'statusOsItem', label: 'Status' },
      {
        key: 'stockSituation',
        label: 'Situação',
        render: renderStockSituation,
      },
      { key: 'exchangeApprovalStatus', label: 'Aprovação do excedente' },
    ]

    switch (type) {
      case 'ALUGUEL':
      case 'RESERVA':
        setEquipColumns([
          { key: 'contract_item', label: 'Item do Contrato', width: '40%' },
          { key: 'address', label: 'Endereço', width: '30%' },
          { key: 'progress_label', label: 'Progresso (Movimentado)' },
          ...commonEquip,
        ])
        break

      case 'DEVOLUÇÃO':
        setEquipColumns([
          { key: 'contract_item', label: 'Item do Contrato', width: '40%' },
          { key: 'equipment', label: 'Equipamento', width: '40%' },
          ...commonEquip,
        ])
        break

      case 'SUBSTITUIÇÃO':
        setEquipColumns([
          { key: 'contract_item', label: 'Item do Contrato', width: '40%' },
          { key: 'equipment', label: 'Equipamento', width: '40%' },
          ...commonEquip,
        ])
        break
      case 'AVULSO':
        setEquipColumns([
          { key: 'type', label: 'Tipo de Equipamento', width: '30%' },
          { key: 'address', label: 'Endereço', width: '30%' },
          { key: 'progress_label', label: 'Progresso (Movimentado)' },
          ...commonEquip,
        ])
        break

      default:
        break
    }
  }, [osDetails])

  const refreshOsItens = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['contractOsItemsEquipments'] })
    queryClient.invalidateQueries({ queryKey: ['contractOsItemsProducts'] })
  }, [queryClient])

  // Marca/desmarca o item da requisição como aguardando compra (sem estoque)
  const handleStockStatusChange = useCallback(
    async (osItem, stockStatus) => {
      await baseServicePut(
        entities.serviceOrders.serviceOrderItemStockStatus(
          serviceOrderId,
          osItem.id
        ),
        { stock_status: stockStatus }
      )
      refreshOsItens()
      // Reflete na coluna "Situação" dos dashboards (almoxarifado/expedição)
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    [baseServicePut, serviceOrderId, refreshOsItens, queryClient]
  )

  const canUpdateStockStatus =
    canAccess('Atualizar itens de ordens de serviço') ||
    canAccess('Criar itens de movimentos')

  const inactivateOsItemMutation = useMutation({
    mutationFn: async ({ osItem, removed_quantity }) => {
      showLoader()
      // * Em casos de requisição sem contrato
      if (!contractId && serviceOrderId) {
        const response = await baseServiceDeleteWithParams(
          entities.serviceOrders.serviceOrderItemDelete(
            serviceOrderId,
            osItem.id
          ),
          {
            removed_quantity,
          },
          {}
        )
        return response
      }
      // * Em casos de requisição com contrato
      else if (contractId && serviceOrderId) {
        const response = await baseServiceDeleteWithParams(
          entities.contracts.contractServiceOrderItemDelete(
            contractId,
            serviceOrderId,
            osItem.id
          ),
          {
            removed_quantity,
          },
          {}
        )
        return response
      }
    },
    onSuccess: (response) => {
      if (response?.status === 200) refreshOsItens()
    },
    onError: (err) => {
      console.log('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  // Mutação para excluir o extra do equipamento
  const deleteExtraMutation = useMutation({
    mutationFn: async ({ extra, parentRow, quantityToRemove }) => {
      showLoader()

      // 1. Usar o nome correto definido no entities.js
      // 2. Passar serviceOrderId (da URL/Estado), parentRow.id (o item) e extra.id
      return await baseServiceDel(
        entities.serviceOrders.serviceOrderItemExtraDelete(
          serviceOrderId, // ID da OS que você já tem via useParams()
          parentRow.id, // ID do ServiceOrderItem
          extra.id // ID do Extra
        ),
        { params: { quantity: quantityToRemove } }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['contractOsItemsEquipments'])
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const restoreExtraMutation = useMutation({
    mutationFn: async ({ extra, parentRow, quantityToRestore }) => {
      showLoader()
      return await baseServicePut(
        entities.serviceOrders.serviceOrderItemExtraRestore(
          serviceOrderId,
          parentRow.id,
          extra.id
        ),
        { quantity: quantityToRestore }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['contractOsItemsEquipments'])
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const actions = useMemo(
    () => [
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver itens de ordem de serviço',
        onClick: (osItem) =>
          navigate(
            `/aluguel/requisicoes/${serviceOrderId}/itens/${osItem.id}/detalhes`
          ),
        show: (osItem) => !osItem.isDeleted,
      },
      {
        id: 'markOutOfStock',
        icon: faBoxOpen,
        title: 'Marcar como Sem estoque',
        buttonClass: 'btn-warning',
        onClick: (osItem) =>
          handleStockStatusChange(osItem, 'awaiting_purchase'),
        show: (osItem) =>
          !blockAction &&
          !osItem.isDeleted &&
          canUpdateStockStatus &&
          osItem.stockStatus !== 'awaiting_purchase',
      },
      {
        id: 'markInStock',
        icon: faCheckCircle,
        title: 'Marcar como Com estoque',
        buttonClass: 'btn-success',
        onClick: (osItem) => handleStockStatusChange(osItem, 'in_stock'),
        show: (osItem) =>
          !blockAction &&
          !osItem.isDeleted &&
          canUpdateStockStatus &&
          osItem.stockStatus === 'awaiting_purchase',
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir itens de ordens de serviço',
        onClick: (osItem) =>
          showConfirmation(
            osItem,
            `Você tem certeza que deseja excluir?`,
            async (removed_quantity) => {
              try {
                await inactivateOsItemMutation.mutateAsync({
                  osItem,
                  removed_quantity,
                })
              } catch (error) {
                console.log('Erro ao excluir item:', {
                  status: error.status,
                  message: error.message,
                })
              }
            },
            { modalAction: 'deleteItem' }
          ),
        show: (osItem) =>
          !blockAction &&
          !osItem.isDeleted &&
          (osItem.statusOsItemId === GenericStatusEnum.NOT_ATTENDED ||
            osItem.statusOsItemId === GenericStatusEnum.PARTIALLY_ATTENDED) &&
          canAccess('Excluir itens de ordens de serviço'),
      },
    ],
    [
      navigate,
      serviceOrderId,
      showConfirmation,
      inactivateOsItemMutation,
      blockAction,
      canAccess,
      handleStockStatusChange,
      canUpdateStockStatus,
    ]
  )

  const [detailsOrderServiceSection] = detailsOrderServiceGlobalFields

  const [fields, setFields] = useState(detailsOrderServiceSection.fields)
  const { updateField, removeFields } = useSectionFields()

  // * Ajustar os campos quando não houver contrato a requisição
  useEffect(() => {
    let updatedFields = [...detailsOrderServiceSection.fields]
    if (!contractId) {
      updatedFields = removeFields({
        arrayDependente: detailsOrderServiceSection.fields,
        field_id: ['contract'],
      })

      updatedFields = updateField({
        arrayDependente: updatedFields,
        field_id: ['recipient_customer'],
        updates: {
          fullWidth: true,
        },
      })
    }

    setFields(updatedFields)
  }, [contractId, detailsOrderServiceSection.fields, removeFields, updateField])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Detalhes da Requisição`}
        showBackButton={true}
        backUrl={`/aluguel/requisicoes`}
      />

      {osDetails?.situation === OrderSituationEnum.CANCELLED && (
        <Box sx={{ my: 2 }}>
          <Alert severity="warning">
            <Typography sx={{ fontSize: '1rem' }}>
              A requisição não foi aprovada pela gerência. Ela permanecerá
              disponível apenas para consulta, não sendo permitidas novas
              alterações.
            </Typography>
          </Alert>
          <hr />
        </Box>
      )}

      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          sections={[
            {
              ...detailsOrderServiceSection.section,
              fields,
            },
          ]}
          formData={formData}
          readOnly
        />

        <ListHeader
          title="Listas de Itens da Requisição"
          buttonText="Adicionar Item"
          buttonLink={
            contractId
              ? `/aluguel/requisicoes/${serviceOrderId}/itens/adicionar?contract_id=${contractId}`
              : `/aluguel/requisicoes/${serviceOrderId}/itens/adicionar`
          }
          // buttonIsNotVisible={osDetails?.status?.name === "TOTALMENTE ATENDIDA"}
          canAccess={canAccess}
          permission={'Criar itens de ordens de serviço'}
          buttonDisabled={blockAction}
        />

        <Box sx={{ my: 2 }}>
          <hr />
        </Box>

        <ListHeader
          title="Equipamentos"
          canAccess={canAccess}
          buttonIsNotVisible
        />

        <TableWithInactiveFilter
          data={equipments || []}
          columns={equipColumns}
          collapseConfig={equipmentCollapseConfig}
          actions={actions}
          totalPages={equipmentsTotalPages}
          currentPage={equipmentsPagination.page - 1}
          onPageChange={equipmentsChangePage}
          rowsPerPage={equipmentsPagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            equipmentsSetPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={equipmentsApplyUrlFilters}
          type={'collapse'}
          defaultCollapseOpen
          rowColorConfig={{
            colorMap: genericStatusColorMap,
            propertyKey: 'statusOsItem',
          }}
          showFilterInactive={false}
          isLoading={isLoadingOsItensEquipments}
          isFetching={isFetchingOsItensEquipments}
        />

        <ListHeader buttonIsNotVisible title="Insumos" canAccess={canAccess} />
        <TableWithInactiveFilter
          data={products || []}
          columns={productsColumns}
          actions={actions}
          totalPages={productsTotalPages}
          currentPage={productsPagination.page - 1}
          onPageChange={productsChangePage}
          rowsPerPage={productsPagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            productsSetPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={productsApplyUrlFilters}
          type="default"
          rowColorConfig={{
            colorMap: genericStatusColorMap,
            propertyKey: 'statusOsItem',
          }}
          showFilterInactive={false}
          isLoading={isLoadingOsItensProducts}
          isFetching={isFetchingOsItensProducts}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={(quantity) => handleConfirmAction(quantity)}
          text={action.text}
          showInput={true}
          maxQuantity={
            action?.modal?.modalAction === 'deleteItem'
              ? selectedItem?.quantity
                ? selectedItem?.quantity - selectedItem?.max_extra_atended // Se for equipamento
                : selectedItem?.quantity_remaining // Se for insumo
              : action?.modal?.modalAction === 'deleteExtra'
                ? selectedItem?.fulfilled_quantity
                : selectedItem?.removed_quantity
          }
        />
      </div>
    </MainLayout>
  )
}

export default DetailsOrderServicesPage
